import { QueryClient, useQueryClient } from "@tanstack/react-query";
import createFetchClient from "openapi-fetch";
import createClient from "openapi-react-query";
import { AoApiPaths, LendingPaths, PublicApiPaths } from "@lending/types";
import { LENDING_AUTH_MODE_HEADER, getDigitalBankingCsrfToken } from "./authMode";
import { type LendingAuthMode } from "../contexts/LendingHostContext";
import { useLendingHost } from "../contexts/LendingHostContext";

const LENDING_CSRF_QUERY_KEY = ["lendingCsrfToken"] as const;
const AO_CSRF_QUERY_KEY = ["accountOpeningCsrfToken"] as const;
const SAFE_METHODS = ["GET", "HEAD", "OPTIONS", "TRACE"];
const INVALID_CSRF_TOKEN_MESSAGE = "Invalid CSRF token";

// Shared origin for every client below (lending, public_api, and ao_api).
const API_BASE_URL = `${import.meta.env.VITE_PUBLIC_API_URL}`;

const lendingFetchClient = createFetchClient<LendingPaths>({
  baseUrl: API_BASE_URL,
  credentials: "include",
});

// For lending API requests that need no CSRF token, and deliberately no auth mode header
// either: these endpoints are either unauthenticated (plaid_user_account) or gated on
// IsNotDigitalBankingUser, which requires an *un*authenticated request — sending the header
// would authenticate the Digital Banking user and fail that permission.
const $baseApi = createClient(lendingFetchClient);

async function getLendingCsrfToken() {
  const { response, data } = await lendingFetchClient.GET("/v2/lending/csrf");
  if (!response.ok) {
    throw new Error("Failed to fetch lending CSRF token");
  }
  return data?.token ?? "";
}

async function getAccountOpeningCsrfToken() {
  const response = await fetch(`${API_BASE_URL}/v1/csrf/`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to fetch account opening CSRF token");
  }

  const csrfToken = response.headers.get("X-CSRFTOKEN");

  if (!csrfToken) {
    throw new Error("Missing account opening CSRF token header");
  }

  return csrfToken;
}

async function isInvalidCsrfResponse(response: Response) {
  if (response.status !== 403) {
    return false;
  }

  try {
    const data = (await response.clone().json()) as { detail?: string; message?: string };
    return (
      data.detail === INVALID_CSRF_TOKEN_MESSAGE || data.message === INVALID_CSRF_TOKEN_MESSAGE
    );
  } catch {
    return false;
  }
}

function getLendingCsrfQueryOptions() {
  return {
    queryKey: LENDING_CSRF_QUERY_KEY,
    queryFn: getLendingCsrfToken,
    staleTime: 60 * 60 * 1000,
  };
}

function getAccountOpeningCsrfQueryOptions() {
  return {
    queryKey: AO_CSRF_QUERY_KEY,
    queryFn: getAccountOpeningCsrfToken,
    staleTime: 60 * 60 * 1000,
  };
}

function createFetchWithCsrf(
  queryClient: QueryClient,
  getCsrfQueryOptions: () => {
    queryKey: readonly string[];
    queryFn: () => Promise<string>;
    staleTime: number;
  },
  // Only the lending API dispatches on the auth mode. Omitted for account opening, which
  // keeps its own synchronizer token in every mode.
  authMode?: LendingAuthMode
) {
  return async function fetchWithCsrf(init: Request) {
    const csrfQueryOptions = getCsrfQueryOptions();
    const method = init?.method?.toUpperCase() ?? "GET";
    const requiresCsrf = !SAFE_METHODS.includes(method);

    const headers = new Headers(init.headers);
    if (authMode) {
      headers.set(LENDING_AUTH_MODE_HEADER, authMode);
    }

    if (requiresCsrf && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    if (!requiresCsrf) {
      return fetch(init, {
        credentials: "include",
        headers,
      });
    }

    // Digital Banking has no lending Session to hold a synchronizer token, so the API
    // relies on DRF's SessionAuthentication having enforced Django's own CSRF instead.
    // That means echoing Django's cookie rather than fetching a lending token — and there
    // is no rotate-and-retry, because Django's token does not change per request.
    if (authMode === "digital_banking") {
      const djangoCsrfToken = getDigitalBankingCsrfToken();
      if (!djangoCsrfToken) {
        // Sending an empty header just earns an opaque 403. There is nothing to retry
        // against either, unlike the synchronizer token below, so say what is wrong.
        throw new Error("Missing Django CSRF cookie");
      }

      headers.set("X-CSRFToken", djangoCsrfToken);
      return fetch(init, {
        credentials: "include",
        headers,
      });
    }

    const retryRequest = init.clone();
    const csrfToken = await queryClient.fetchQuery(csrfQueryOptions);

    // No session yet means an empty token. Caching it would send `X-CSRFToken: ""` on every
    // later write, which 403s as `permission_denied` — not something the retry below spots.
    if (!csrfToken) {
      queryClient.removeQueries({ queryKey: csrfQueryOptions.queryKey });
    }

    headers.set("X-CSRFToken", csrfToken);

    const response = await fetch(init, {
      credentials: "include",
      headers,
    });

    if (!(await isInvalidCsrfResponse(response))) {
      return response;
    }

    await queryClient.invalidateQueries({ queryKey: csrfQueryOptions.queryKey });

    const refreshedCsrfToken = await queryClient.fetchQuery(csrfQueryOptions);
    headers.set("X-CSRFToken", refreshedCsrfToken);

    return fetch(retryRequest, {
      credentials: "include",
      headers,
    });
  };
}

// For lending API requests that require a CSRF token
function useApi() {
  const queryClient = useQueryClient();
  const { authMode } = useLendingHost();

  const fetchClient = createFetchClient<LendingPaths>({
    baseUrl: API_BASE_URL,
    fetch: createFetchWithCsrf(queryClient, getLendingCsrfQueryOptions, authMode),
  });

  return createClient(fetchClient);
}

function useAoApi() {
  const queryClient = useQueryClient();

  const fetchClient = createFetchClient<AoApiPaths>({
    baseUrl: API_BASE_URL,
    fetch: createFetchWithCsrf(queryClient, getAccountOpeningCsrfQueryOptions),
  });

  return createClient(fetchClient);
}

const publicApiFetchClient = createFetchClient<PublicApiPaths>({
  baseUrl: API_BASE_URL,
});
const $publicApi = createClient(publicApiFetchClient);

// Account-opening verification endpoints (locate_applicant, verify) are
// unauthenticated server-side (no session auth), so there is no CSRF to attach.
// Plain typed client for imperative calls — skips the token fetch the CSRF-aware
// useAoApi() would otherwise do.
const aoApiFetchClient = createFetchClient<AoApiPaths>({
  baseUrl: API_BASE_URL,
  credentials: "include",
});

// The CSRF-aware lending API client returned by `useApi()`. Exported so query
// definitions can be shared as `queryOptions` factories across hooks.
type LendingApiClient = ReturnType<typeof useApi>;

export { $publicApi, $baseApi, aoApiFetchClient, useAoApi, useApi, LENDING_CSRF_QUERY_KEY };
export type { LendingApiClient };
